<script>
/**
 * 감정항목 등록/수정 모달 (본사 전용).
 */
import { createAppraisalItem, updateAppraisalItem } from '@/api/appraisalItem.js'

export default {
  name: 'AppraisalSpotItemFormDialog',

  props: {
    modelValue: { type: Boolean, default: false },
    /** 수정 시 행 데이터 */
    editData: { type: Object, default: null },
    /** 등록 시 기본 현물구분 (탭) */
    defaultSpotType: { type: String, default: 'GOLD' },
  },

  emits: ['update:modelValue', 'saved'],

  data() {
    return {
      spotTypeOptions: [
        { title: '금', value: 'GOLD' },
        { title: '은', value: 'SILVER' },
      ],
      form: {
        spotType: 'GOLD',
        appraisalItemType: '',
        appraisalItemName: '',
        standardPercent: '',
        conversionPercent: '',
      },
      saving: false,
      errorMsg: '',
    }
  },

  computed: {
    defaultItemType() {
      return this.defaultSpotType === 'SILVER' ? '은' : '24K'
    },
    isEditMode() {
      return !!this.editData?.appraisalItemCode
    },
    dialogTitle() {
      return this.isEditMode ? '감정항목 수정' : '감정항목 등록'
    },
    visible: {
      get() { return this.modelValue },
      set(val) { this.$emit('update:modelValue', val) },
    },
  },

  watch: {
    modelValue(val) {
      if (!val) return
      this.errorMsg = ''
      if (this.isEditMode) {
        this.form = {
          spotType: this.editData.spotType === 'SILVER' ? 'SILVER' : 'GOLD',
          appraisalItemType: this.editData.appraisalItemType || '',
          appraisalItemName: this.editData.appraisalItemName || '',
          standardPercent:
            this.editData.standardPercent != null && this.editData.standardPercent !== ''
              ? String(this.editData.standardPercent)
              : '',
          conversionPercent:
            this.editData.conversionPercent != null && this.editData.conversionPercent !== ''
              ? String(this.editData.conversionPercent)
              : '',
        }
      } else {
        this.form = {
          spotType: this.defaultSpotType === 'SILVER' ? 'SILVER' : 'GOLD',
          appraisalItemType: this.defaultItemType,
          appraisalItemName: '',
          standardPercent: '',
          conversionPercent: '',
        }
      }
    },
  },

  methods: {
    parsePercent() {
      const s = String(this.form.standardPercent ?? '').trim()
      if (s === '') return null
      const n = Number(s)
      if (Number.isNaN(n)) return undefined
      return n
    },

    parseConversionPercent() {
      const s = String(this.form.conversionPercent ?? '').trim()
      if (s === '') return null
      const n = Number(s)
      if (Number.isNaN(n)) return undefined
      return n
    },

    async handleSave() {
      this.errorMsg = ''
      if (!this.form.appraisalItemType?.trim()) {
        this.errorMsg = '감정항목유형을 입력해 주세요.'
        return
      }
      if (!this.form.appraisalItemName?.trim()) {
        this.errorMsg = '감정항목명을 입력해 주세요.'
        return
      }
      const pct = this.parsePercent()
      if (pct === undefined) {
        this.errorMsg = '기준비율은 숫자(0~1)이거나 비워 주세요.'
        return
      }
      if (pct != null && (pct < 0 || pct > 1)) {
        this.errorMsg = '기준비율은 0 이상 1 이하여야 합니다.'
        return
      }
      const conv = this.parseConversionPercent()
      if (conv === undefined) {
        this.errorMsg = '환산비율은 숫자(0~1)이거나 비워 주세요.'
        return
      }
      if (conv != null && (conv < 0 || conv > 1)) {
        this.errorMsg = '환산비율은 0 이상 1 이하여야 합니다.'
        return
      }

      const payload = {
        spotType: this.form.spotType,
        appraisalItemType: this.form.appraisalItemType.trim(),
        appraisalItemName: this.form.appraisalItemName.trim(),
        standardPercent: pct,
        conversionPercent: conv,
      }

      this.saving = true
      try {
        let res
        if (this.isEditMode) {
          res = await updateAppraisalItem(this.editData.appraisalItemCode, payload)
        } else {
          res = await createAppraisalItem(payload)
        }
        if (res.data?.code === 0) {
          this.$emit('saved')
          this.visible = false
        } else {
          this.errorMsg = res.data?.message || '저장 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[AppraisalSpotItemFormDialog] save', e)
        this.errorMsg = '저장 중 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    handleClose() {
      this.visible = false
    },
  },
}
</script>

<template>
  <v-dialog v-model="visible" max-width="520" scrollable>
    <v-card rounded="lg">
      <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-2">
        {{ dialogTitle }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-5">
        <v-alert v-if="errorMsg" type="error" density="compact" class="mb-4" rounded="lg">
          {{ errorMsg }}
        </v-alert>

        <v-text-field
          v-if="isEditMode"
          :model-value="editData.appraisalItemCode"
          label="감정항목코드"
          variant="outlined"
          density="compact"
          readonly
          class="mb-3"
        />

        <v-select
          v-model="form.spotType"
          :items="spotTypeOptions"
          item-title="title"
          item-value="value"
          label="현물구분"
          variant="outlined"
          density="compact"
          hide-details="auto"
          class="mb-3"
        />

        <v-text-field
          v-model="form.appraisalItemType"
          label="감정항목유형 (예: 24K, 4N, 은, 기타)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          maxlength="50"
          class="mb-3"
        />

        <v-text-field
          v-model="form.appraisalItemName"
          label="감정항목명"
          variant="outlined"
          density="compact"
          hide-details="auto"
          maxlength="255"
          class="mb-3"
        />

        <v-text-field
          v-model="form.standardPercent"
          label="기준비율 (소수, 예: 0.995 — 비우면 미적용)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          placeholder="비움 = NULL"
          class="mb-3"
        />

        <v-text-field
          v-model="form.conversionPercent"
          label="환산비율 (소수 0~1 — 지점 환산중량 계산에 사용, 비우면 저장 시 미설정)"
          variant="outlined"
          density="compact"
          hide-details="auto"
          placeholder="비움 = NULL"
        />
      </v-card-text>
      <v-divider />
      <v-card-actions class="pa-4 justify-end ga-2">
        <v-btn variant="text" @click="handleClose">취소</v-btn>
        <v-btn color="primary" variant="flat" :loading="saving" @click="handleSave">
          저장
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
